package co.maytes.standupbot.dashboard.features.templates

import co.maytes.standupbot.shared.template.SectionsConfigDto

data class TemplatePreset(
    val id: String,
    val emoji: String,
    val name: String,
    val tagline: String,
    val description: String,
    val promptBody: String,
    val sectionsConfig: SectionsConfigDto,
)

object TemplatePresets {
    val all: List<TemplatePreset> = listOf(
        classicScrum(),
        blockerFocused(),
        asyncWeekly(),
        retroLite(),
        codeReviewHeavy(),
    )

    fun findById(id: String): TemplatePreset? = all.firstOrNull { it.id == id }

    private fun classicScrum() = TemplatePreset(
        id = "classic-scrum",
        emoji = "🧱",
        name = "Classic scrum",
        tagline = "Yesterday · Today · Blockers",
        description = "The dependable three-question daily. Clean, skimmable, human-sounding.",
        promptBody = """
            You are writing a concise morning standup for {{team_name}} at {{workspace_name}}.

            OUTPUT SHAPE — per teammate, in this exact structure. Omit any empty section entirely (do NOT print an empty header).

            *@display-name*
            :white_check_mark: Yesterday: <one short phrase per item, ticket id inline, comma-separated if 2, bullet list if 3+>
            :arrow_forward: Today: <same shape>
            :construction: Blockers: <same shape — only when there are real blockers>

            RULES (very important):
            - Collapse state-transition churn. If a ticket cycled Backlog → In Progress → Canceled → Ready For Dev → Canceled → Done within the window, report only the FINAL state ("MOB-23 shipped"). Never dump the full chain.
            - Every line is a human phrase, not a raw dump. Say "Shipped MOB-23 (Stripe Google Pay config)" not "*Completed:* [MOB-23]: …".
            - Never emit literal markdown headers like "*Completed:*" or "*In progress:*" inside entries. Those are noise.
            - A ticket that appears in both Today and Blockers is fine, but the Today line should then add " — blocked" rather than repeating the ticket under Blockers.
            - If a person has no activity, skip them entirely. Don't print an empty block for them.
            - If NO person has activity, output a single line: "🤫 Quiet day — no Linear activity in the last 24 hours."

            RAW DATA (use this, do not echo it):
            Yesterday's issue updates per person:
            {{yesterday_items}}

            Today's in-flight / started issues per person:
            {{today_items}}

            Blockers flagged per person:
            {{blockers}}

            Output plain Slack text using Slack's mrkdwn (single asterisks for bold, not double). No preamble, no sign-off, no horizontal rules.
        """.trimIndent(),
        sectionsConfig = SectionsConfigDto(includeYesterday = true, includeToday = true, includeBlockers = true),
    )

    private fun blockerFocused() = TemplatePreset(
        id = "blocker-first",
        emoji = "🚧",
        name = "Blocker-first",
        tagline = "Lead with what's stuck",
        description = "Surface blockers at the top so standup energy goes to unblocking, then summarise work in a single line.",
        promptBody = """
            You are the standup editor for {{team_name}}. Your job is to make blockers impossible to miss.

            Put BLOCKERS at the very top. If there are none, say "No blockers — ship it."

            🚧 BLOCKERS:
            {{blockers}}

            Then condense each person's yesterday + today into ONE line. Keep ticket ids, drop filler words.

            YESTERDAY → TODAY (one line per person):
            Yesterday:
            {{yesterday_items}}
            Today:
            {{today_items}}

            Output plain Slack text. No greetings, no wrap-up.
        """.trimIndent(),
        sectionsConfig = SectionsConfigDto(includeYesterday = true, includeToday = true, includeBlockers = true),
    )

    private fun asyncWeekly() = TemplatePreset(
        id = "async-weekly",
        emoji = "🌏",
        name = "Async weekly",
        tagline = "For distributed teams",
        description = "Weekly rollup vibe — highlights, priorities for the week, and flags. No daily churn.",
        promptBody = """
            Write a weekly async check-in for {{team_name}} at {{workspace_name}}. Tone: thoughtful, calm, skimmable.

            Use H3-style bold headers.

            **Last week's highlights**
            Summarise the meaningful ships from:
            {{yesterday_items}}

            **This week's focus**
            Distill priorities from:
            {{today_items}}

            **Flags & asks**
            Translate blockers into explicit asks ("need review on X", "waiting on Y"):
            {{blockers}}

            Close with a single line: "Reply in thread with any clarifications."
        """.trimIndent(),
        sectionsConfig = SectionsConfigDto(includeYesterday = true, includeToday = true, includeBlockers = true),
    )

    private fun retroLite() = TemplatePreset(
        id = "retro-lite",
        emoji = "🪞",
        name = "Retro-lite",
        tagline = "Wins + learnings, no tomorrow",
        description = "A reflective cadence: what landed and what was learned. Skip the forward-looking plan — use your planning doc.",
        promptBody = """
            You are writing a reflective standup for {{team_name}}.

            For each teammate, extract from their Linear activity:

            ✅ SHIPPED:
            {{yesterday_items}}

            💡 LEARNED / NOTICED:
            Infer a short observation from their commentary + ticket transitions. If nothing stands out, omit.

            🚧 STUCK ON:
            {{blockers}}

            Deliberately skip "today" — planning lives elsewhere. Keep the whole post under 150 words.
        """.trimIndent(),
        sectionsConfig = SectionsConfigDto(includeYesterday = true, includeToday = false, includeBlockers = true),
    )

    private fun codeReviewHeavy() = TemplatePreset(
        id = "code-review-heavy",
        emoji = "🔍",
        name = "Code-review heavy",
        tagline = "Reviews + ships emphasis",
        description = "Good for standups where review throughput is the bottleneck. Surfaces waiting-on-review items and today's review queue.",
        promptBody = """
            Daily engineering standup for {{team_name}}. Emphasise review flow.

            For each teammate, produce:

            **Shipped & reviewed yesterday:**
            {{yesterday_items}}

            **In flight / needs review today:**
            {{today_items}}

            **Blocked on review or input:**
            {{blockers}}

            Call out any ticket that has been "In review" for > 24h with a ⏰ emoji.
            Keep the format tight — Slack-ready, no preamble, no sign-off.
        """.trimIndent(),
        sectionsConfig = SectionsConfigDto(includeYesterday = true, includeToday = true, includeBlockers = true),
    )
}
